/**
 * SUBSCRIPTION MUTATIONS - NO OPTIMISTIC UPDATES
 *
 * ⚠️  IMPORTANT: This file intentionally does NOT use optimistic updates
 * for payment/subscription operations, as these require server confirmation
 * for security and payment processing reasons.
 *
 * For all other mutations in the app, see /hooks/queries/README.md
 * for the required optimistic update patterns.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import {
  createCheckoutSession,
  createPortalSession,
  checkSubscriptionStatus,
} from '../../lib/stripe';
import { subscriptionKeys } from './subscriptionKeys';
import { CustomerSubscription, Price } from './subscriptionTypes';
import { handleError } from '../../utils/errorHandling';
import { logger } from '../../utils/logger';
import { db } from '../../lib/offline-db';
import { useConnectionState } from '../useConnectionState';

/**
 * Hook to fetch the current user's subscription
 * Equivalent to fetchSubscription() in the original Zustand store
 */
export function useUserSubscription(userId: string | undefined) {
  const { isOnline } = useConnectionState();

  return useQuery({
    queryKey: userId ? subscriptionKeys.customer(userId) : subscriptionKeys.user(),
    queryFn: async () => {
      try {
        if (!userId) {
          const {
            data: { user },
          } = await supabase.auth.getUser();
          if (!user) {
            throw new Error('No authenticated user');
          }
          userId = user.id;
        }

        logger.component('Fetching subscription for user', { userId });

        // If online, fetch from Supabase
        if (isOnline) {
          const { data, error } = await supabase
            .from('customers')
            .select('customer_stripe_id, subscription_status, subscription_tier, updated_at')
            .eq('user_id', userId)
            .maybeSingle();

          if (error) {
            console.error('Supabase query error:', error);
            throw error;
          }

          console.log('Subscription data:', data);

          // Store in IndexedDB for offline access
          if (data) {
            await db.customerSubscriptions.put({
              ...data,
              user_id: userId,
            });
          }

          return data as CustomerSubscription | null;
        }

        // If offline, fetch from IndexedDB
        const offlineData = await db.customerSubscriptions.where('user_id').equals(userId).first();

        return offlineData as CustomerSubscription | null;
      } catch (error) {
        console.error('Error fetching subscription:', error);
        handleError('fetchSubscription', error, {
          extraContext: { userId },
          showNotification: false,
        });
        throw error;
      }
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch available subscription prices
 * Equivalent to fetchPrices() in the original Zustand store
 * Note: This preserves the original behavior of trying to fetch from a 'prices' table
 */
export function useSubscriptionPrices() {
  const { isOnline } = useConnectionState();

  return useQuery({
    queryKey: subscriptionKeys.prices(),
    queryFn: async () => {
      try {
        // If online, fetch from Supabase (preserving original behavior)
        if (isOnline) {
          const { data, error } = await supabase
            .from('prices')
            .select('*')
            .eq('active', true)
            .neq('metadata->hidden', true)
            .order('unit_amount', { ascending: true });

          if (error) {
            console.error('Error fetching prices:', error);
            throw error;
          }

          // Store in IndexedDB for offline access
          if (data && data.length > 0) {
            await db.prices.bulkPut(data);
          }

          return data as Price[];
        }

        // If offline, fetch from IndexedDB
        const offlineData = await db.prices.where('active').equals(true).toArray();

        return offlineData as Price[];
      } catch (error) {
        console.error('Error fetching prices:', error);
        handleError('fetchPrices', error, {
          showNotification: false,
        });
        throw error;
      }
    },
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook to check the subscription status including plan details
 */
export function useSubscriptionStatus(userId: string | undefined) {
  return useQuery({
    queryKey: [...subscriptionKeys.user(), 'status', userId],
    queryFn: async () => {
      if (!userId) {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('No authenticated user');
        }
        userId = user.id;
      }

      return checkSubscriptionStatus(userId);
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create a subscription
 * Equivalent to subscribe() in the original Zustand store
 */
export function useCreateSubscription() {
  return useMutation({
    mutationFn: async (priceId: string) => {
      await createCheckoutSession(priceId);
      return true;
    },
    onError: error => {
      console.error('Error creating subscription:', error);
      handleError('Creating subscription', error, {
        showNotification: true,
        notificationPrefix: 'Subscription error',
      });
    },
  });
}

/**
 * Hook to manage an existing subscription
 * Equivalent to manageSubscription() in the original Zustand store
 */
export function useManageSubscription() {
  return useMutation({
    mutationFn: async () => {
      await createPortalSession();
      return true;
    },
    onError: error => {
      console.error('Error managing subscription:', error);
      handleError('Managing subscription', error, {
        showNotification: true,
        notificationPrefix: 'Subscription management error',
      });
    },
  });
}

/**
 * Hook to refresh the subscription data
 * Equivalent to refreshSubscription() in the original Zustand store
 */
export function useRefreshSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (_userId: string) => {
      return true;
    },
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.customer(userId) });
      queryClient.invalidateQueries({ queryKey: [...subscriptionKeys.user(), 'status', userId] });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.prices() });
    },
  });
}
